import classNames from 'classnames/bind';
import ButtonLink from 'src/components/ui-kit/button/__link';
import Icon, { IconTheme, IconVariant } from 'src/components/ui-kit/icon/icon';
import TableCell from 'src/components/ui-kit/table/_cell';
import TableRow from 'src/components/ui-kit/table/_row';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './table.module.scss';
import { Listing } from 'src/entities/listing';
import Link from 'next/link';
import { ButtonVariant } from 'src/components/ui-kit/button/button';
import { useSelector } from 'react-redux';
import { AuthSelectors } from 'src/store/auth';

const classesContext = classNames.bind(styles);

export default function ListingsTableRow({
  listing,
  className,
  ...restProps
}: { listing: Listing } & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>): JSX.Element {
  const { t } = useTranslation(['components:pages:listings:table']);
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);

  return (
    <TableRow
      className={classesContext(['listings_item', 'table_row', { listings_item_bold: listing.isBold }, className])}
      {...restProps}
    >
      <TableCell className={classesContext(['table_cell', 'table_cell__id', 'table_cell__desktop'])}>
        {listing.id}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__bid', 'table_cell__desktop'])}>
        {listing.startingBid ?? t(isAuthenticated ? 'TEXT_PLEASE_MAKE_OFFER' : 'TEXT_APPROVED_BUYERS_ONLY')}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__state', 'table_cell__desktop'])}>
        {listing.state.code}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__county', 'table_cell__desktop'])}>
        {listing.county.name}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__acres', 'table_cell__desktop'])}>
        {listing.netAcres}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__cash', 'table_cell__desktop'])}>
        {listing.averageMonthlyCashFlow ? listing.formatUsAverageMonthlyCashFlow : t('TEXT_N_A')}
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__tablet'])}>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>{t('TEXT_LISTING_ID')}</div>
          <div className={classesContext('listings_item_col', 'listings_item_col__id')}>{listing.id}</div>
        </div>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>
            {t('TEXT_STARTING_BID')}
          </div>
          <div className={styles.listings_item_col}>
            {listing.startingBid ?? t(isAuthenticated ? 'TEXT_PLEASE_MAKE_OFFER' : 'TEXT_APPROVED_BUYERS_ONLY')}
          </div>
        </div>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>{t('TEXT_STATE')}</div>
          <div className={styles.listings_item_col}>{listing.state.code}</div>
        </div>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>{t('TEXT_COUNTY')}</div>
          <div className={styles.listings_item_col}>{listing.county.name}</div>
        </div>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>{t('TEXT_NET_ACRES')}</div>
          <div className={styles.listings_item_col}>{listing.netAcres}</div>
        </div>
        <div className={styles.listings_item_row}>
          <div className={classesContext('listings_item_col', 'listings_item_col__title')}>{t('TEXT_CASH_FLOW')}</div>
          <div className={styles.listings_item_col}>
            {listing.averageMonthlyCashFlow ? listing.formatUsAverageMonthlyCashFlow : t('TEXT_N_A')}
          </div>
        </div>
      </TableCell>
      <TableCell className={classesContext(['table_cell', 'table_cell__actions'])}>
        <div className={styles.listings_item_actions}>
          <Link
            href={listing.url}
            passHref>
            <ButtonLink
              className={styles.listings_item_button}
              variant={listing.isBold ? ButtonVariant.TERTIARY : ButtonVariant.SECONDARY}
            >
              <span className={styles.listings_item_actions_text}>{t('BUTTON_VIEW')}</span>
              <Icon
                variant={IconVariant.NEXT}
                theme={IconTheme.WHITE} />
            </ButtonLink>
          </Link>
        </div>
      </TableCell>
    </TableRow>
  );
}
